/* ===========================================================================
 * cro-deck.js — the slide deck for /pages/how-it-works
 *
 * The page is a deck, not a document. Every .cro-slide is lifted into a
 * container fixed to the viewport, page scrolling is switched off, and one
 * gesture moves one slide with a transition this file controls from start to
 * finish. That is the point: scroll-snapping got us a hybrid where the page
 * sometimes scrolled and sometimes jumped, and the entrance animations fired
 * on the way past rather than on arrival.
 *
 * WHY THE DOM IS REPARENTED. The slides are not siblings in the markup — the
 * four stages live inside one section wrapper, and the hero, system and range
 * each have their own — so there is no single element to translate. Rather
 * than have the sections know about each other, this collects .cro-slide in
 * document order and moves them into one track. Each slide carries `hh cro`
 * itself for exactly this reason; it has to keep its tokens and its background
 * once it is no longer inside the wrapper that had them.
 *
 * THE FOOTER MOVES TOO. It is rendered after </main>, and the deck is fixed to
 * the viewport, so anything left below would be unreachable — there is no page
 * scroll left to reach it with. It is appended into the last slide, which is
 * the one slide that scrolls internally because the bundle grid is taller than
 * a screen.
 *
 * WHERE IT DOES NOT RUN. Under prefers-reduced-motion, and below 1000x740.
 * A deck is a promise that a slide fits the screen; on a phone the panel and
 * the animation are stacked and a stage does not, so hijacking the scroll
 * there would hide content behind a gesture with no scrollbar to reveal it.
 * Those cases keep the ordinary document, which is why none of the layout CSS
 * depends on this script having run.
 * ======================================================================== */
(function () {
  'use strict';

  var MIN_W = 1000;
  var MIN_H = 740;
  var DURATION = 760; // keep in step with --cro-slide-dur in cro-guide.css
  var WHEEL_THRESHOLD = 28;
  var SWIPE_THRESHOLD = 55;

  function allowed() {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    } catch (e) {
      return false;
    }
    return window.innerWidth >= MIN_W && window.innerHeight >= MIN_H;
  }

  function Deck(main) {
    this.main = main;
    this.slides = [].slice.call(document.querySelectorAll('.cro-slide'));
    this.index = 0;
    this.locked = false;
    this.wheelAccum = 0;
    this.wheelReset = 0;
    this.built = false;
  }

  Deck.prototype.build = function () {
    if (this.built || this.slides.length < 2) return;
    this.built = true;

    this.measureTop();

    var deck = document.createElement('div');
    deck.className = 'cro-deck';
    this.deck = deck;

    for (var i = 0; i < this.slides.length; i++) deck.appendChild(this.slides[i]);
    this.main.appendChild(deck);

    /* Everything rendered after </main> — the footer group, and anything an
     * app has appended — would be stranded below a deck fixed to the viewport,
     * with no page scroll left to reach it. The last slide is the only one
     * tall enough to scroll, so it is the only one that can hold it.
     *
     * Taken by position rather than by selector: the footer group's id is
     * generated, and an app block added later should come along too. Scripts
     * and other invisible nodes are left where they are — moving a <script>
     * that has already run does nothing useful and can re-trigger one. */
    var last = this.slides[this.slides.length - 1];
    var tail = [];
    var node = this.main.nextElementSibling;
    while (node) {
      var tag = node.tagName;
      if (tag !== 'SCRIPT' && tag !== 'TEMPLATE' && tag !== 'NOSCRIPT' && tag !== 'STYLE') {
        tail.push(node);
      }
      node = node.nextElementSibling;
    }

    if (tail.length) {
      /* Wrapped, because the last slide is a column flex box: dropped in
       * loose, the footer would become another flex item sharing the vertical
       * space with the content instead of sitting underneath it. */
      var wrap = document.createElement('div');
      wrap.className = 'cro-tail';
      for (var t = 0; t < tail.length; t++) wrap.appendChild(tail[t]);
      last.appendChild(wrap);
    }

    document.documentElement.classList.add('cro-deck-on');
    this.watchTop();
    this.buildSteps();
    this.buildScale();
    this.paint(true);
    this.bind();
  };

  /* Where the deck starts: the bottom of the header group.
   *
   * THIS HAS TO BE RE-MEASURED, NOT MEASURED ONCE. The free-shipping ticker is
   * an app section that injects itself into the header after the page has
   * painted, so a single reading taken at DOMContentLoaded is 45px short and
   * the deck sits underneath the bar it is supposed to start below. Reading
   * main's own top keeps working after the deck has gone fixed, because main
   * is still in flow immediately after the header.
   *
   * A ResizeObserver on the header covers the injection whenever it lands; the
   * load and resize handlers cover browsers without one and window changes. */
  Deck.prototype.measureTop = function () {
    var top = Math.round(this.main.getBoundingClientRect().top + window.pageYOffset);
    if (top !== this.top) {
      this.top = top;
      document.documentElement.style.setProperty('--cro-deck-top', top + 'px');
    }
  };

  Deck.prototype.watchTop = function () {
    var self = this;
    var remeasure = function () {
      self.measureTop();
    };

    window.addEventListener('load', remeasure);
    window.addEventListener('resize', remeasure);

    if (!('ResizeObserver' in window)) return;
    var ro = new ResizeObserver(remeasure);
    var node = this.main.previousElementSibling;
    while (node) {
      ro.observe(node);
      node = node.previousElementSibling;
    }
  };

  /* The step controls at the top and bottom of the screen. Each names the
   * slide it goes to, taken from that slide's own heading, so the next move is
   * legible instead of something the reader has to find by scrolling. They are
   * buttons rather than links because they drive the deck, and a link would
   * offer a middle-click and a new tab that lead nowhere useful. */
  Deck.prototype.buildSteps = function () {
    var self = this;
    var ARROW =
      '<svg class="cro-step__arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M12 5v14M6 13l6 6 6-6" stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';

    function make(dir) {
      var el = document.createElement('button');
      el.type = 'button';
      /* `hh cro` for the same reason as the scale: appended outside both, so
       * it needs them to see the tokens. */
      el.className = 'hh cro cro-step cro-step--' + (dir < 0 ? 'prev' : 'next');
      el.innerHTML = ARROW + '<span class="cro-step__label"></span>';
      /* The up arrow is the same glyph rotated, and the rotation is applied in
       * the CSS keyframes rather than here — an animation on transform would
       * overwrite an inline one and leave it pointing down. */
      el.addEventListener('click', function () {
        self.go(self.index + dir);
      });
      self.deck.parentNode.appendChild(el);
      return el;
    }

    this.stepPrev = make(-1);
    this.stepNext = make(1);
  };

  Deck.prototype.paintSteps = function () {
    if (!this.stepPrev) return;

    var self = this;
    function set(el, i) {
      var slide = self.slides[i];
      if (!slide) {
        el.hidden = true;
        return;
      }
      var label = slide.getAttribute('data-cro-label') || 'Section ' + (i + 1);
      el.hidden = false;
      el.querySelector('.cro-step__label').textContent = label;
      el.setAttribute('aria-label', 'Go to ' + label);
    }

    set(this.stepPrev, this.index - 1);
    set(this.stepNext, this.index + 1);
  };

  /* THE SCALE. The deck's position indicator, and the page's one bold idea.
   *
   * It was a rail of identical dots, which is the stock furniture of every
   * full-screen deck and says nothing about the product. The cups have volume
   * graduations printed up the side, so the indicator is that: a rule with a
   * major tick per stage, and the current one grows and fills.
   *
   * The ticks are labelled with stage names, not millilitres. Printing 30/60/90
   * would look more like the cup and mean nothing, and a number that refers to
   * no quantity is decoration dressed as data.
   *
   * It also took over from the eyebrow. Every panel used to open with a
   * tracked-out STARTING SOLIDS, which named the stage a third time after the
   * header nav and the step controls had already done it. */
  Deck.prototype.buildScale = function () {
    var scale = document.createElement('nav');
    /* `hh cro` as well as its own class. The deck appends this to #MainContent,
     * which is OUTSIDE both .hh and .cro — so without them every token it uses
     * (--hh-line-strong, --hh-lav-deep, --cro-measure) resolves to nothing and
     * the graduations render invisible. Same trap as --cro-field being scoped
     * to .cro while html and body needed it. */
    scale.className = 'hh cro cro-scale';
    scale.setAttribute('aria-label', 'Stages');

    var self = this;
    this.stops = [];

    for (var i = 0; i < this.slides.length; i++) {
      (function (i) {
        var label = self.slides[i].getAttribute('data-cro-label') || 'Section ' + (i + 1);
        var stop = document.createElement('button');
        stop.type = 'button';
        stop.className = 'cro-scale__stop';
        stop.innerHTML =
          '<span class="cro-scale__tick"></span>' +
          '<span class="cro-scale__name"></span>';
        /* The name goes in as text, not markup: it comes from a theme setting
         * and could be anything someone types into the editor. */
        stop.querySelector('.cro-scale__name').textContent = label;
        stop.setAttribute('aria-label', label);
        stop.addEventListener('click', function () {
          self.go(i);
        });
        scale.appendChild(stop);
        self.stops.push(stop);
      })(i);
    }

    this.deck.parentNode.appendChild(scale);
    this.scale = scale;
  };

  Deck.prototype.paint = function (immediate) {
    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var state = i === this.index ? 'is-active' : i < this.index ? 'is-before' : 'is-after';
      slide.classList.remove('is-active', 'is-before', 'is-after');
      slide.classList.add(state);

      /* inert rather than only visually hidden: without it a keyboard tab runs
       * straight through six slides' worth of links the reader cannot see. */
      if (i === this.index) {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('inert', '');
        slide.setAttribute('aria-hidden', 'true');
      }

      if (this.stops && this.stops[i]) {
        this.stops[i].classList.toggle('is-active', i === this.index);
        if (i === this.index) {
          this.stops[i].setAttribute('aria-current', 'true');
        } else {
          this.stops[i].removeAttribute('aria-current');
        }
      }
    }

    if (immediate) {
      // Skip the very first transition so the page does not animate on arrival.
      var deck = this.deck;
      deck.classList.add('cro-deck--instant');
      // Two frames: one to apply the class, one to let the state land under it.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          deck.classList.remove('cro-deck--instant');
        });
      });
    }

    var id = this.slides[this.index].id;
    if (id && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', '#' + id);
    }

    this.syncNav(id);
    this.paintSteps();
    document.dispatchEvent(
      new CustomEvent('cro:slide', { detail: { index: this.index, id: id } })
    );
  };

  Deck.prototype.syncNav = function (id) {
    var nav = document.querySelector('[data-cro-nav]');
    if (!nav) return;
    var links = nav.querySelectorAll('a[href^="#"]');
    for (var i = 0; i < links.length; i++) {
      var on = links[i].getAttribute('href').slice(1) === id;
      links[i].classList.toggle('is-active', on);
      if (on) {
        links[i].setAttribute('aria-current', 'true');
      } else {
        links[i].removeAttribute('aria-current');
      }
    }
  };

  Deck.prototype.go = function (next) {
    next = Math.max(0, Math.min(this.slides.length - 1, next));
    if (next === this.index || this.locked) return;

    this.index = next;
    this.locked = true;
    this.paint(false);

    var self = this;
    setTimeout(function () {
      self.locked = false;
      self.wheelAccum = 0;
    }, DURATION);
  };

  Deck.prototype.byId = function (id) {
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].id === id) return i;
    }
    return -1;
  };

  /* The last slide is taller than the screen and scrolls inside itself, so a
   * wheel event there belongs to the slide until it has been read to the end.
   * Only hand it back to the deck at the edge the reader is pushing against. */
  Deck.prototype.scrollableEdge = function (dir) {
    var slide = this.slides[this.index];
    if (slide.scrollHeight <= slide.clientHeight + 2) return true;
    if (dir > 0) return slide.scrollTop + slide.clientHeight >= slide.scrollHeight - 2;
    return slide.scrollTop <= 2;
  };

  Deck.prototype.bind = function () {
    var self = this;

    window.addEventListener(
      'wheel',
      function (e) {
        if (self.locked) {
          e.preventDefault();
          return;
        }

        var dir = e.deltaY > 0 ? 1 : -1;
        if (!self.scrollableEdge(dir)) return; // let the slide scroll itself

        e.preventDefault();

        /* Trackpad momentum arrives as a long tail of small deltas. Summing
         * them and resetting on a pause turns one flick into one slide, rather
         * than firing on the first event and then again on the tail. */
        var now = Date.now();
        if (now - self.wheelReset > 220) self.wheelAccum = 0;
        self.wheelReset = now;
        self.wheelAccum += e.deltaY;

        if (Math.abs(self.wheelAccum) < WHEEL_THRESHOLD) return;
        self.wheelAccum = 0;
        self.go(self.index + dir);
      },
      { passive: false }
    );

    window.addEventListener('keydown', function (e) {
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          if (!self.scrollableEdge(1)) return;
          e.preventDefault();
          self.go(self.index + 1);
          break;
        case 'ArrowUp':
        case 'PageUp':
          if (!self.scrollableEdge(-1)) return;
          e.preventDefault();
          self.go(self.index - 1);
          break;
        case 'Home':
          e.preventDefault();
          self.go(0);
          break;
        case 'End':
          e.preventDefault();
          self.go(self.slides.length - 1);
          break;
      }
    });

    var touchY = null;
    window.addEventListener(
      'touchstart',
      function (e) {
        touchY = e.touches[0].clientY;
      },
      { passive: true }
    );

    window.addEventListener(
      'touchmove',
      function (e) {
        if (touchY === null || self.locked) return;
        var dy = touchY - e.touches[0].clientY;
        if (Math.abs(dy) < SWIPE_THRESHOLD) return;
        var dir = dy > 0 ? 1 : -1;
        if (!self.scrollableEdge(dir)) return;
        touchY = null;
        self.go(self.index + dir);
      },
      { passive: true }
    );

    /* Any in-page link — the header nav, the hero's stage chips, the system
     * section's "Find your bundle" — moves the deck instead of scrolling. */
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var i = self.byId(id);
      if (i < 0) return;
      e.preventDefault();
      self.go(i);
    });

    window.addEventListener('hashchange', function () {
      var i = self.byId(window.location.hash.slice(1));
      if (i >= 0) self.go(i);
    });

    /* Crossing the size threshold means the deck is no longer a promise we can
     * keep, or has become one we can. Reloading is blunt but it is honest —
     * tearing the deck down and rebuilding it live is a great deal of state to
     * get wrong for a case that only happens when a window is resized. */
    var wasAllowed = true;
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var now = allowed();
        if (now !== wasAllowed) window.location.reload();
      }, 250);
    });
  };

  function init() {
    if (!allowed()) return;

    var main = document.getElementById('MainContent');
    if (!main || !document.querySelector('.cro-slide')) return;
    if (main.dataset.croDeck === 'true') return;
    main.dataset.croDeck = 'true';

    var deck = new Deck(main);

    /* Resolve the incoming hash BEFORE build(), not after.
     *
     * build() paints, and paint() writes the current slide's id into the
     * address bar. Read the hash afterwards and you read back whatever that
     * write left there, not what the reader arrived with -- so every deep link
     * landed on slide one. It only started happening when the opening slide
     * gained an id of its own; until then paint() had nothing to write and the
     * hash survived by luck. */
    var start = deck.byId(window.location.hash.slice(1));
    if (start > 0) deck.index = start;

    deck.build();

    window.croDeck = deck;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
